import React, { FC, useCallback } from "react";

import { addWeeks, differenceInCalendarDays, startOfToday } from "date-fns/esm";
import isBefore from "date-fns/isBefore";
import { useIntl } from "react-intl";

import DatePicker from "@app/component/input/DatePicker";
import { LabeledSwitch } from "@app/component/input/LabeledSwitch";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./SupportAccess.module.scss";

const DATE_LIMIT = 90;

interface Props {
  onChange?: (value: null | string) => void;
  value?: null | string;
}

const SupportAccess: FC<Props> = ({ onChange, value }) => {
  const intl = useIntl();

  let enableUntil = value ? parseApiDate(value) : false;
  if (!isValidDate(enableUntil)) {
    enableUntil = false;
  }
  const enabled = enableUntil !== false && !isBefore(enableUntil, startOfToday());

  const handleEnabledChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        onChange?.(null);
      } else {
        const defaultDate = addWeeks(startOfToday(), 2);
        onChange?.(defaultDate.toISOString());
      }
    },
    [onChange]
  );

  const handleDateChange = useCallback(
    (value: Date | null) => {
      onChange?.(value?.toISOString() ?? null);
    },
    [onChange]
  );

  const isDateDisabled = useCallback((date: Date) => {
    return (
      isBefore(date, startOfToday()) ||
      Math.abs(differenceInCalendarDays(startOfToday(), date)) > DATE_LIMIT
    );
  }, []);

  return (
    <div className={styles.container}>
      <LabeledSwitch
        checked={enabled}
        description={intl.formatMessage({
          id: "settings.organizationDetails.data.supportAccess.switch.description",
          defaultMessage:
            "Sometimes debugging and other requests require Customer Support to access your org’s hidden (PHI) data. Best practice is to only enable access long enough to resolve your request. ",
          description: "Settings organization details - Data - Grant Support Access - description",
        })}
        onChange={handleEnabledChange}
        title={intl.formatMessage({
          id: "settings.organizationDetails.data.supportAccess.switch.title",
          defaultMessage: "Grant Support Access",
          description: "Settings organization details - Data - Grant Support Access",
        })}
      />
      {enabled && (
        <>
          <DatePicker
            className={styles.date}
            disabledDate={isDateDisabled}
            onChange={handleDateChange}
            value={enableUntil || undefined}
          />
          <span className={styles.comment}>
            {intl.formatMessage(
              {
                id: "settings.organizationDetails.data.supportAccess.datePicker.comment",
                defaultMessage:
                  "Select up to {count} {count, plural, one {day} other {days}} from now",
                description: "Grant Support Access date picker comment",
              },
              { count: DATE_LIMIT }
            )}
          </span>
        </>
      )}
    </div>
  );
};

export default SupportAccess;
